import * as React from 'react';

import { translate } from '~l10n/client';
import { Badge } from '~shared/components/Badge';
import { Dialog, DialogTitle } from '~shared/components/Dialog';
import { IconBlankQuiz } from '~shared/components/icons/large/IconBlankQuiz/IconBlankQuiz';
import { IconCopyPasteQuestions } from '~shared/components/icons/large/IconCopyPasteQuestions/IconCopyPasteQuestions';
import { IconExtractQuestions } from '~shared/components/icons/large/IconExtractQuestions/IconExtractQuestions';
import { IconGenerateQuestions } from '~shared/components/icons/large/IconGenerateQuestions/IconGenerateQuestions';
import { IconImportQuestions } from '~shared/components/icons/large/IconImportQuestions/IconImportQuestions';
import { IconAI } from '~shared/components/icons/standard';
import { TileButton } from '~shared/components/TileButton';
import { proCheck } from '~teacher/util/proCheck';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  generateQuestions: () => void;
  extractQuestionsFromDoc: () => void;
  importFromText: () => void;
  createBlank: () => void;
  importQuiz: () => void;
}

export function useAddQuizDialog() {
  const [isAddQuizDialogOpen, setIsAddQuizDialogOpen] = React.useState(false);

  const openAddQuizDialog = () => {
    setIsAddQuizDialogOpen(true);
  };
  const closeAddQuizDialog = () => setIsAddQuizDialogOpen(false);

  return { isAddQuizDialogOpen, openAddQuizDialog, closeAddQuizDialog };
}

export function AddQuizDialog({
  isOpen,
  onClose,
  importQuiz,
  createBlank,
  generateQuestions,
  extractQuestionsFromDoc,
  importFromText,
}: Props) {
  const canUploadAiFiles = proCheck.canUploadAiFiles();

  return (
    <Dialog
      title={translate('Add Quiz')}
      onClose={onClose}
      open={isOpen}
      size="content"
      id="add-quiz"
    >
      {({ headingRef, title }) => {
        return (
          <div className="flex flex-col h-full">
            <div
              ref={headingRef}
              id="add-quiz-title"
              className="pt-6 pb-4 shadow-inset-border-b"
            >
              <DialogTitle weight="bold">{title}</DialogTitle>
            </div>
            <div className="flex flex-col gap-4 py-12 mx-auto max-w-3xl">
              <div className="flex content-center">
                <span className="text-display font-display font-700 leading-8 text-blue-900">
                  {translate('Create with AI')}
                </span>
                <IconAI className="text-orange-600" />
              </div>
              <TileButton
                layout="horizontal"
                icon={<IconGenerateQuestions />}
                title={translate('Generate Questions')}
                description={translate(
                  'Create questions using a prompt and/or a file upload.'
                )}
                onClick={generateQuestions}
              />

              <div className="pt-6">
                <span className="text-display font-display font-700 leading-8 text-blue-900">
                  {translate('Import Questions')}
                </span>
              </div>
              <TileButton
                layout="horizontal"
                icon={<IconCopyPasteQuestions />}
                title={translate('Copy-Paste Questions')}
                description={translate(
                  'Import questions by pasting them from another resource.'
                )}
                onClick={importFromText}
              />
              <TileButton
                layout="horizontal"
                icon={<IconExtractQuestions />}
                title={
                  <>
                    {translate('Extract Questions from Document')}
                    {!canUploadAiFiles && (
                      <Badge tone="pro" className="font-sans">
                        Pro
                      </Badge>
                    )}
                  </>
                }
                description={translate(
                  "Upload a file, and we'll find and extract the questions in it."
                )}
                onClick={extractQuestionsFromDoc}
              />
              <TileButton
                layout="horizontal"
                icon={<IconImportQuestions />}
                title={translate(
                  'Import Questions from Code or Excel Template'
                )}
                description={translate(
                  'Import a quiz from another user or from an excel template.'
                )}
                onClick={importQuiz}
              />

              <div className="pt-6">
                <span className="text-display font-display font-700 leading-8 text-blue-900">
                  {translate('Start From Scratch')}
                </span>
              </div>
              <TileButton
                layout="horizontal"
                icon={<IconBlankQuiz />}
                title={translate('Blank Quiz')}
                description={translate(
                  'Jump right in and build something great.'
                )}
                onClick={createBlank}
              />
            </div>
          </div>
        );
      }}
    </Dialog>
  );
}
